import { useState, useEffect, useRef } from 'preact/hooks';
import { themeTokens } from '../themeTokens';
import { render } from 'preact';
import { t } from '../i18n';
import { getConfig, resolveTheme } from '../config';
import { store } from '../store';
import { navigateToView } from '../navigation';
import { SignupView } from './SignupView';
import { DeliveryGate } from './DeliveryGate';
import { ProfileView } from './ProfileView';
import { CheckoutPage } from './CheckoutPage';
import { CheckoutQnrPage } from './CheckoutQnrPage';
import { CheckoutConfirmPage } from './CheckoutConfirmPage';
import { CheckoutStepper } from './CheckoutStepper';
import { OrderSuccessPage } from './OrderSuccessPage';
import { OrderReturnPage } from './OrderReturnPage';
import { ReferralPage } from './ReferralPage';
import { BonusAccountPage } from './BonusAccountPage';
import { BonusTransactionsPage } from './BonusTransactionsPage';
import { SubscriptionsPage } from './SubscriptionsPage';
import { CartPage } from './CartPage';
import { CheckoutMiniCart } from './CheckoutMiniCart';
import { OrdersView } from './OrdersView';
import { DiscountsView } from './DiscountsView';
import { TicketsView } from './TicketsView';
import { DeleteAccountView } from './DeleteAccountView';
import { ForgotPasswordView } from './ForgotPasswordView';
import { ResetPasswordView } from './ResetPasswordView';
import { login as apiLogin } from '../api';
import { refreshAuthAndCart } from '../api';
import { emit } from '../analytics';
import { consumeSubsAuthRedirectNotice } from '../subsAuthNotice';
import { ensureCheckoutReturnTo, navigateAfterAuth, completeAuthPageRedirect, isAuthRoutePath, openContextualLogin, openContextualSignup } from '../returnTo';
import { isInlineViewOnCurrentPage, preferAuthModalOnCurrentPage } from '../inlinePages';
import type { AccountView } from '../store';
import { lockBodyScroll, unlockBodyScroll } from '../utils/scrollLock';

const CHECKOUT_VIEWS: AccountView[] = ['checkout', 'checkout-qnr', 'checkout-confirm'];

const TITLE_KEYS: Record<NonNullable<AccountView>, string> = {
  discounts: 'title.discounts',
  'bonus-transactions': 'title.bonusTransactions',
  account: 'title.account',
  'bonus-account': 'title.bonusAccount',
  'referral-tree': 'title.referralTree',
  orders: 'title.orders',
  subscriptions: 'title.subscriptions',
  tickets: 'title.tickets',
  'account-delete': 'title.accountDelete',
  signup: 'title.signup',
  cart: 'title.cart',
  catalog: 'title.catalog',
  checkout: 'title.checkout',
  'checkout-qnr': 'title.checkoutQnr',
  'checkout-confirm': 'title.checkoutConfirm',
  'order-success': 'title.orderSuccess',
  'order-return': 'title.orderReturn',
  'checkout-auth': 'title.checkoutAuth',
  'checkout-login': 'title.checkoutLogin',
  'forgot-password': 'title.forgotPassword',
  'reset-password': 'title.resetPassword',
};

const Portal = ({ children }: { children: import('preact').ComponentChildren }) => {
  const containerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = document.createElement('div');
    el.className = 'shop-widget-mount shop-widget-portal';
    const config = getConfig();
    const theme = resolveTheme(config.themePreset, config.theme);
    const map: Record<string, string> = {
      primary: '--sw-primary',
      secondary: '--sw-secondary',
      header_links: '--sw-header-links',
      header_links_hover: '--sw-header-links-hover',
      bg: '--sw-bg',
      text: '--sw-text',
      muted: '--sw-muted',
      border: '--sw-border',
      radius: '--sw-radius',
      radiusModal: '--sw-radius-modal',
      font: '--sw-font',
      fontSize: '--sw-font-size',
      lineHeight: '--sw-line-height',
      fontWeight: '--sw-font-weight',
      letterSpacing: '--sw-letter-spacing',
      fontSizeSm: '--sw-font-size-sm',
      font_size_sm: '--sw-font-size-sm',
      shadow: '--sw-shadow',
      shadowSm: '--sw-shadow-sm',
      shadowModal: '--sw-shadow-modal',
      surface_bg: '--sw-surface-bg',
      surface_border: '--sw-surface-border',
      surface_shadow: '--sw-surface-shadow',
      card_text: '--sw-card-text',
      button_bg: '--sw-button-bg',
      button_background_color: '--sw-button-bg',
      button_text: '--sw-button-text',
      button_text_color: '--sw-button-text',
      button_border: '--sw-button-border',
      button_border_color: '--sw-button-border',
      button_hover_bg: '--sw-button-hover-bg',
      button_hover_text: '--sw-button-hover-text',
      button_hover_border: '--sw-button-hover-border',
      button_focus_bg: '--sw-button-focus-bg',
      button_focus_text: '--sw-button-focus-text',
      button_active_bg: '--sw-button-active-bg',
      button_active_text: '--sw-button-active-text',
      success: '--sw-success',
      danger: '--sw-danger',
    };
    for (const [key, cssVar] of Object.entries(map)) {
      const value = (theme as Record<string, unknown>)[key];
      if (value != null && value !== '') el.style.setProperty(cssVar, String(value));
    }
    const isModeDisabled = (value: unknown) => {
      const mode = String(value ?? '').trim().toLowerCase();
      return mode === 'none' || mode === 'hide' || mode === 'hidden' || mode === 'off' || mode === 'false' || mode === '0';
    };
    if (isModeDisabled((theme as Record<string, unknown>).card_border)) el.style.setProperty('--sw-card-border', 'none');
    if (isModeDisabled((theme as Record<string, unknown>).card_radius)) el.style.setProperty('--sw-card-radius', '0');
    if (isModeDisabled((theme as Record<string, unknown>).card_shadow)) el.style.setProperty('--sw-card-shadow', 'none');
    const rootStyle = getComputedStyle(document.documentElement);
    const bridge: Array<[string, string]> = [
      ['--sl-button-bg', '--sw-button-bg'],
      ['--sl-button-text', '--sw-button-text'],
      ['--sl-button-border', '--sw-button-border'],
      ['--sl-button-hover-bg', '--sw-button-hover-bg'],
      ['--sl-button-hover-text', '--sw-button-hover-text'],
      ['--sl-button-hover-border', '--sw-button-hover-border'],
      ['--sl-button-focus-bg', '--sw-button-focus-bg'],
      ['--sl-button-focus-text', '--sw-button-focus-text'],
      ['--sl-button-active-bg', '--sw-button-active-bg'],
      ['--sl-button-active-text', '--sw-button-active-text'],
      ['--sl-success', '--sw-success'],
      ['--sl-danger', '--sw-danger'],
    ];
    for (const [slVar, swVar] of bridge) {
      const val = rootStyle.getPropertyValue(slVar).trim();
      if (val) el.style.setProperty(swVar, val);
    }
    containerRef.current = el;
    document.body.appendChild(el);
    return () => {
      if (containerRef.current) {
        render(null, containerRef.current);
        document.body.removeChild(containerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      render(children, containerRef.current);
    }
  }, [children]);

  return null;
};

export function AccountOverlay() {
  const [view, setView] = useState<AccountView>(store.getAccountView());
  useEffect(() => {
    return store.subscribe(() => setView(store.getAccountView()));
  }, []);

  const close = () => navigateToView(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const open = !!view
    && !isInlineViewOnCurrentPage(view as NonNullable<AccountView>)
    && !(view === 'cart' && getConfig().cartOpenMode === 'side_drawer');
  const activeView = view as NonNullable<AccountView>;

  // ESC to close + focus trap
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    lockBodyScroll();
    requestAnimationFrame(() => {
      const root = modalRef.current;
      if (!root) return;
      const body = root.querySelector('.modal-body');
      const preferred = body?.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]):not(.btn-close), a[href]'
      );
      if (preferred) {
        preferred.focus();
        return;
      }
      root.focus();
    });
    return () => { document.removeEventListener('keydown', onKey); unlockBodyScroll(); };
  }, [open]);

  if (!open) return null;

  const config = getConfig();
  const isWide = activeView === 'account' || activeView === 'cart' || activeView === 'checkout' || activeView === 'checkout-qnr' || activeView === 'checkout-confirm' || activeView === 'order-success' || activeView === 'orders' || activeView === 'bonus-account' || activeView === 'bonus-transactions' || activeView === 'referral-tree' || activeView === 'subscriptions' || activeView === 'tickets';
  const isCompact = activeView === 'forgot-password' || activeView === 'reset-password' || activeView === 'order-return';
  const isMedium = activeView === 'signup' || activeView === 'checkout-login';
  const isCartFlow = ['cart', 'checkout-auth', 'checkout-login', 'checkout', 'checkout-qnr', 'checkout-confirm', 'order-success', 'order-return'].includes(activeView);
  const headerIcon = isCartFlow ? 'bi-cart3' : 'bi-person-circle';
  const modalSize = isCompact ? 'modal-sm' : (isMedium ? 'modal-lg' : (isWide ? 'modal-xl' : 'modal-lg'));
  const defaultMaxWidth = isCompact ? '420px' : (isMedium ? '520px' : (isWide ? '1140px' : '540px'));
  const modalMaxWidth = config.modalMaxWidth ?? defaultMaxWidth;
  const closeOnBackdrop = config.closeOnBackdrop !== false;

  return (
    <Portal>
      <div
        ref={modalRef}
        class="modal fade show d-block"
        tabindex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sw-modal-title"
        style={{ background: themeTokens.overlay, zIndex: 'calc(var(--sw-z-index-base, 1050) + 5)' }}
        onClick={closeOnBackdrop ? close : undefined}
      >
        <div
          class={`modal-dialog modal-dialog-centered ${modalSize}`}
          role="document"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: modalMaxWidth }}
        >
          <div class="modal-content shadow-lg border-0 overflow-hidden" style={{ borderRadius: themeTokens.radiusModal, overflow: 'hidden' }}>
            <div class="modal-header bg-light border-bottom">
              <h5 id="sw-modal-title" class="modal-title fw-bold text-dark"><i class={`bi ${headerIcon} me-2 text-muted`}></i>{t(TITLE_KEYS[activeView])}</h5>
              <button type="button" class="btn-close" aria-label={t('close')} onClick={close}></button>
            </div>
            <div class="modal-body p-4 bg-light">
              <AccountViewContent view={activeView} inline={preferAuthModalOnCurrentPage()} />
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}

function CheckoutAuthChoice() {
  useEffect(() => {
    ensureCheckoutReturnTo();
  }, []);

  return (
    <div class="text-center py-4">
      <p class="mb-4">{t('checkout.authPrompt')}</p>
      <div class="d-flex flex-wrap gap-3 justify-content-center">
        <button type="button" class="btn btn-primary btn-lg" onClick={() => openContextualLogin()}>
          <span style="display:inline-flex;align-items:center;gap:.45rem">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M10 7V5a3 3 0 0 1 3-3h5a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3h-5a3 3 0 0 1-3-3v-2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
              <path d="M15 12H3m0 0 3.5-3.5M3 12l3.5 3.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            {t('checkout.authLogin')}
          </span>
        </button>
        <button type="button" class="btn btn-outline-primary btn-lg" onClick={() => openContextualSignup()}>
          <span style="display:inline-flex;align-items:center;gap:.45rem">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="1.8" />
              <path d="M4 20c0-3.6 3.6-6 8-6s8 2.4 8 6M19 7v4m-2-2h4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
            </svg>
            {t('checkout.authSignup')}
          </span>
        </button>
      </div>
    </div>
  );
}

function getLoginFormStyles() {
  const tk = themeTokens;
  return {
    form: { width: '100%', maxWidth: 'none', margin: '0 auto', padding: '2rem', boxSizing: 'border-box' as const },
    input: { width: '100%', padding: '10px 12px', border: `1px solid ${tk.border}`, borderRadius: tk.radius, fontSize: 'var(--sw-font-size)', lineHeight: 'var(--sw-line-height)', background: tk.bg, color: tk.text, boxSizing: 'border-box' as const },
    label: { display: 'block', marginBottom: '6px', fontWeight: 'var(--sw-font-weight)', fontSize: 'var(--sw-font-size-sm)', color: tk.text },
    link: { color: tk.primary, fontSize: 'var(--sw-font-size-sm)', marginTop: 0, display: 'inline-flex', textAlign: 'center' as const },
  };
}

function CheckoutLoginForm({ config, showTitle, inline }: { config: ReturnType<typeof getConfig>; showTitle?: boolean; inline?: boolean }) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ login?: string; password?: string }>({});
  const [loading, setLoading] = useState(false);
  const loginPendingRef = useRef(false);
  const [subsAuthBanner, setSubsAuthBanner] = useState(false);
  const styles = getLoginFormStyles();

  useEffect(() => {
    if (consumeSubsAuthRedirectNotice()) setSubsAuthBanner(true);
    ensureCheckoutReturnTo();
  }, []);

  const handleRemindPassword = () => {
    navigateToView('forgot-password', inline ? { inline: true } : undefined);
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (loginPendingRef.current) return;
    setError('');
    const fe: { login?: string; password?: string } = {};
    if (!login.trim()) fe.login = t('login.loginRequired');
    if (!password) fe.password = t('login.passwordRequired');
    setFieldErrors(fe);
    if (fe.login || fe.password) return;
    loginPendingRef.current = true;
    setLoading(true);
    try {
      const res = await apiLogin(config, login.trim(), password);
      if (res.status === 200 && res.result) {
        await refreshAuthAndCart(config, store, { force: true });
        emit('sw:login');
        if (typeof window !== 'undefined' && window.location.pathname.includes('/preview-proxy')) {
          const u = new URL(window.location.href);
          const returnPath = u.searchParams.get('sw_return_path');
          if (returnPath) {
            const currentPath = u.searchParams.get('path') ?? '/';
            u.searchParams.delete('sw_return_path');
            if (returnPath !== currentPath) {
              u.searchParams.set('path', returnPath);
              window.location.href = u.toString();
              return;
            }
            window.history.replaceState({}, '', u.toString());
          }
        }
        if (typeof window !== 'undefined' && isAuthRoutePath(window.location.pathname)) {
          completeAuthPageRedirect();
          return;
        }
        navigateAfterAuth(inline);
        return;
      }
      setError(res.error ?? t('login.invalidCredentials'));
    } catch {
      setError(t('api.networkError'));
    } finally {
      loginPendingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate style={styles.form} class={inline ? 'sw-surface-card' : undefined}>
      {subsAuthBanner && (
        <div class="alert alert-info py-2 px-3 small mb-3" role="status" style={{ marginTop: 0 }}>
          {t('inline.subscriptionsAuthRedirect')}
        </div>
      )}
      {showTitle && <h3 style={{ margin: '0 0 1.5rem', fontSize: '1.25rem', color: themeTokens.text }}>{t('login.title')}</h3>}
      <div style={{ marginBottom: '1rem' }}>
        {showTitle && <label style={styles.label}>{t('login.loginLabel')}</label>}
        <input type="text" class="form-control" style={{ ...styles.input, borderColor: fieldErrors.login ? themeTokens.danger : undefined }} autocomplete="username" placeholder={t('login.loginPlaceholder')} aria-label={t('login.loginLabel')} value={login} onInput={(e) => { setLogin((e.target as HTMLInputElement).value); setFieldErrors((f) => ({ ...f, login: undefined })); }} disabled={loading} />
        {fieldErrors.login && <div class="small text-danger mt-1">{fieldErrors.login}</div>}
      </div>
      <div style={{ marginBottom: '1rem' }}>
        {showTitle && <label style={styles.label}>{t('login.passwordLabel')}</label>}
        <input type="password" class="form-control" style={{ ...styles.input, borderColor: fieldErrors.password ? themeTokens.danger : undefined }} autocomplete="current-password" placeholder="••••••••" aria-label={t('login.passwordLabel')} value={password} onInput={(e) => { setPassword((e.target as HTMLInputElement).value); setFieldErrors((f) => ({ ...f, password: undefined })); }} disabled={loading} />
        {fieldErrors.password && <div class="small text-danger mt-1">{fieldErrors.password}</div>}
      </div>
      {error && <div class="alert alert-danger py-2 small">{error}</div>}
      <button type="submit" class="btn btn-primary w-100 mt-3" disabled={loading}>
        {loading ? <span class="spinner-border spinner-border-sm me-2"></span> : null}{t('login.submit')}
      </button>
      <div class="sw-login-links">
        <button type="button" class="sw-link-button" style={styles.link} onClick={handleRemindPassword}>{t('auth.loginModal.remindPassword')}</button>
        <a href="#" style={styles.link} onClick={(e) => { e.preventDefault(); openContextualSignup(); }}>{t('login.noAccount')} {t('login.signupLink')}</a>
      </div>
    </form>
  );
}

export function AccountViewContent({ view, inline }: { view: NonNullable<AccountView>; inline?: boolean }) {
  const config = getConfig();
  const [cartCount, setCartCount] = useState(store.getCartCount());

  useEffect(() => store.subscribe(() => setCartCount(store.getCartCount())), []);

  const isCheckoutFlow = ['cart', 'checkout', 'checkout-qnr', 'checkout-confirm'].includes(view);
  const showCheckoutChrome = isCheckoutFlow && cartCount > 0;

  const content = (() => {
    if (view === 'orders') return <OrdersView config={config} />;
    if (view === 'account') return <ProfileView />;
    if (view === 'discounts') return <DiscountsView />;
    if (view === 'cart') return <CartPage forceClassic />;
    if (view === 'checkout') return <DeliveryGate accountCountry>{() => <CheckoutPage />}</DeliveryGate>;
    if (view === 'checkout-qnr') return <DeliveryGate accountCountry>{() => <CheckoutQnrPage />}</DeliveryGate>;
    if (view === 'checkout-confirm') return <DeliveryGate accountCountry>{() => <CheckoutConfirmPage />}</DeliveryGate>;
    if (view === 'order-success') return <OrderSuccessPage />;
    if (view === 'order-return') return <OrderReturnPage />;
    if (view === 'bonus-account') return <BonusAccountPage />;
    if (view === 'bonus-transactions') return <BonusTransactionsPage />;
    if (view === 'referral-tree') return <ReferralPage />;
    if (view === 'subscriptions') return <SubscriptionsPage />;
    if (view === 'tickets') return <TicketsView config={config} />;
    if (view === 'account-delete') return <DeleteAccountView />;
    if (view === 'checkout-auth') return <DeliveryGate>{() => <CheckoutAuthChoice />}</DeliveryGate>;
    if (view === 'checkout-login') return <CheckoutLoginForm config={config} showTitle={inline} inline={inline} />;
    if (view === 'forgot-password') return <ForgotPasswordView config={config} />;
    if (view === 'reset-password') return <ResetPasswordView config={config} />;
    if (view === 'signup') return <DeliveryGate>{(country, rules) => <SignupView deliveryCountry={country} deliveryRules={rules} checkoutContext={store.getReturnTo() === 'checkout'} onSuccess={() => {
      void (async () => {
        await refreshAuthAndCart(config, store, { force: true });
        navigateAfterAuth(true);
      })();
    }} />}</DeliveryGate>;

    return (
      <div class="text-center py-5">
        <svg width="44" height="44" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ color: 'var(--sw-muted)', marginBottom: '0.75rem' }}>
          <path d="M12 8.8a3.2 3.2 0 1 0 0 6.4 3.2 3.2 0 0 0 0-6.4z" stroke="currentColor" stroke-width="1.8" />
          <path d="M19.4 14a7.8 7.8 0 0 0 .1-2l2-1.5-2-3.4-2.4.7a7.8 7.8 0 0 0-1.7-1l-.3-2.5h-4l-.3 2.5a7.8 7.8 0 0 0-1.7 1l-2.4-.7-2 3.4 2 1.5a7.8 7.8 0 0 0 .1 2l-2 1.5 2 3.4 2.4-.7c.5.4 1.1.8 1.7 1l.3 2.5h4l.3-2.5c.6-.2 1.2-.6 1.7-1l2.4.7 2-3.4-2-1.5z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round" />
        </svg>
        <p class="text-muted mb-0">{t('overlay.underConstruction')}</p>
      </div>
    );
  })();

  return (
    <>
      {showCheckoutChrome && <CheckoutStepper currentStep={view} />}
      {showCheckoutChrome && CHECKOUT_VIEWS.includes(view) && (
        <div class="p-1">
          <CheckoutMiniCart />
        </div>
      )}
      {content}
    </>
  );
}
